import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BaseDynamicFormComponent } from 'src/app/common/components/dynamic-form-v2/components/base-dynamic-form/base-dynamic-form.component';
import { DynamicFormV2Component } from 'src/app/common/components/dynamic-form-v2/dynamic-form-v2.component';
import { DynamicCardType } from 'src/app/common/components/dynamic-form-v2/enums/DynamicCardType';
import { DynamicInputType } from 'src/app/common/components/dynamic-form-v2/enums/DynamicInputType';
import RewardsTrackDTO from '../../../rewards-track/dtos/RewardsTrackDTO';
import DefaultTracks from '../../../season-pass/dtos/defaultTracks';
import { InputFieldType } from 'src/app/common/components/dynamic-form-v2/enums/InputFieldType';
import { DataService } from 'src/app/services/data.service';

enum RewardsTrackType {
  DAILY_1_7 = 1,
  DAILY_30 = 2,
  SEASON_PASS_FREE = 3,
  SEASON_PASS_PAID = 4,
  FRIENDS = 5,
  EO_TRACK = 6,
  COOP = 7,
  MINI_GAMES = 8,
  SEASONAL_PRIZES = 9,
  DAILY_ACTIVITIES = 10,
  PERK_DAILY_REWARD = 11,
  GIFTING_TREE_V2 = 12
}

@Component({
  selector: 'app-reward-track-template-form-v2',
  templateUrl: './reward-track-template-form-v2.component.html',
  styleUrls: ['./reward-track-template-form-v2.component.sass']
})
export class RewardTrackTemplateFormV2Component extends BaseDynamicFormComponent implements OnInit  
{
  @ViewChild(DynamicFormV2Component) dynamicFormComponent: DynamicFormV2Component;
  @Input() rewardsTrackRecord: RewardsTrackDTO = new RewardsTrackDTO();
  @Input() isChildComponent: boolean = false;
  @Input() disableTypeField: boolean = false;
  @Output() onRewardsTrackRecordChange = new EventEmitter<any>();
  defaultTracks: DefaultTracks = new DefaultTracks();
  templateTracks: any[] = [];

  // All available track types for the dropdown
  private readonly allTrackTypes = [
    { name: 'Login Rewards - 7 days', value: RewardsTrackType.DAILY_1_7 },
    { name: 'Login Rewards - Day 30', value: RewardsTrackType.DAILY_30 },
    { name: 'Garden Pass - Free', value: RewardsTrackType.SEASON_PASS_FREE },
    { name: 'Garden Pass - Paid', value: RewardsTrackType.SEASON_PASS_PAID },
    { name: 'Friends Invited', value: RewardsTrackType.FRIENDS },
    { name: 'Endless Offer', value: RewardsTrackType.EO_TRACK },
    { name: 'Co-Op', value: RewardsTrackType.COOP },
    { name: 'Mini Games', value: RewardsTrackType.MINI_GAMES },
    { name: 'Seasonal Prizes', value: RewardsTrackType.SEASONAL_PRIZES },
    { name: 'Daily Activities', value: RewardsTrackType.DAILY_ACTIVITIES },
    { name: 'Perk Daily Reward', value: RewardsTrackType.PERK_DAILY_REWARD },
    { name: 'Gifting Tree V2', value: RewardsTrackType.GIFTING_TREE_V2 }
  ];

    // Field mapping for each track type
    private readonly trackTypeFields: { [key in RewardsTrackType]?: string[] } = {
      [RewardsTrackType.DAILY_1_7]: ['dailyRewardDay1', 'dailyRewardDay2', 'dailyRewardDay3', 'dailyRewardDay4', 'dailyRewardDay5', 'dailyRewardDay6', 'dailyRewardDay7', 'stageMilestone'],
      [RewardsTrackType.DAILY_30]: ['dailyRewardDay30', 'stageMilestone'],
      [RewardsTrackType.SEASON_PASS_FREE]: ['free', 'stageMilestone'],
      [RewardsTrackType.SEASON_PASS_PAID]: ['paid', 'stageMilestone'],
      [RewardsTrackType.FRIENDS]: ['friends'],
      [RewardsTrackType.EO_TRACK]: ['EOrewards', 'EObundles'],
      [RewardsTrackType.COOP]: ['coop', 'stageMilestone'],
      [RewardsTrackType.MINI_GAMES]: ['miniGamesRewards'],
      [RewardsTrackType.SEASONAL_PRIZES]: ['seasonalPrizes'],
      [RewardsTrackType.DAILY_ACTIVITIES]: ['dailyActivitiesRewards'],
      [RewardsTrackType.PERK_DAILY_REWARD]: ['perkDailyReward'],
      [RewardsTrackType.GIFTING_TREE_V2]: ['giftingTreeV2Rewards', 'stageMilestone']
  };

    // Helper function to get fields to copy based on track type
    private getFieldsToCopy(trackType: RewardsTrackType): string[] {
      return this.trackTypeFields[trackType] || [];
    }
  
    // Helper function to extract relevant fields from template
    private extractRelevantFields(template: any, trackType: RewardsTrackType): { [key: string]: any } {
      const relevantFields = this.getFieldsToCopy(trackType);
      const extractedFields: { [key: string]: any } = {};
      
      relevantFields.forEach(field => {
        if (template[field] !== undefined) {
          extractedFields[field] = template[field];
        }
      });
  
      return extractedFields;
    }

  constructor(private dataService: DataService) {
      super();
  }

    /**
   * Fetches template reward tracks
   */
    async getTemplateTracks() {
      const templateTracks = await this.dataService.getAllOfTypeAsync('rewards-track', {
        query: { isTemplate: true },
        sort: { createdAt: -1 },
        includeFullRewards: true  // Include full reward arrays (coop, free, paid, etc.)
      });
  
      return templateTracks;
    }
  

    async ngOnInit()
  {
        // Fetch template tracks
        this.templateTracks = await this.getTemplateTracks();

        // isTemplate is always true for reward track templates
        this.rewardsTrackRecord.isTemplate = true;

        this.fields =
        [
          {
            title: "Rewards Track Template",
            fields:
            [
              {
                name: "Name",
                inputType: DynamicInputType.BaseInputField,
                inputTypeFields: [InputFieldType.TextInput],
                clearField: false,
                key: 'name',
                isRequired: true,
                label: 'Template Name',
                disabled: false,
                placeholder: 'Enter a descriptive name for this template',
                helperText: 'Templates are used as starting points when creating new reward tracks. When users select this track type, they will see this template\'s rewards pre-filled.'
              },
              {
                name: "Type",
                inputType: DynamicInputType.BaseInputField,
                inputTypeFields: [InputFieldType.Dropdown],
                clearField: true,
                key: 'type',
                label: 'Track Type',
                filter: true,
                autoDisplayFirst: false,
                optionLabel: 'name',
                optionValue: 'value',
                isRequired: true,
                disabled: this.disableTypeField,
                options:
                {
                  fieldName: 'type',
                  values: this.allTrackTypes
                },
                helperText: 'Select the type of reward track this template is for. Only one template per type is recommended.'
              },
              {
                name: "Track",
                inputType: DynamicInputType.RewardsTrackInputComponent,
                key: 'track',
                label: 'Reward Track Configuration'
              },
            ],
            type: DynamicCardType.Card
          }
        ]
        this.title = "Rewards Track Template";
        this.viewRoute = 'rewards-track-templates';
        this.isLoading = false;
  }

  /**
   * Emits changes comming from child component
   * to parent component.
   *
   * @param event Envent comming from child component.
   */
  onChange(event: any)
  { 
    if(event.field == 'type') {
      // Find the selected template track
      const selectedTemplate = this.templateTracks.find(track => track.type === event.value);
      
      if (selectedTemplate) {
        // Save the current values from the event record (which contains the form's current state)
        const currentName = event.record?.name || this.rewardsTrackRecord.name;
        const currentEnv = event.record?.env || this.rewardsTrackRecord.env;
        
        // Extract only the relevant fields based on track type
        const relevantFields = this.extractRelevantFields(selectedTemplate, event.value);
        
        const updatedRecord = {
          ...this.rewardsTrackRecord,
          ...relevantFields, // Apply only the relevant template fields
          type: event.value,
          name: currentName || '', // Preserve the original name
          env: currentEnv, // Preserve the original env value
          isTemplate: true, // Always enforce isTemplate for reward track templates
        };

        // Update the record reference to trigger change detection
        this.rewardsTrackRecord = updatedRecord;
      } else {
        // Handle default tracks for Garden Pass types
        switch (event.value) {
          case RewardsTrackType.SEASON_PASS_FREE:
            this.rewardsTrackRecord.free = this.defaultTracks.FreeDefaultTrack;
            this.rewardsTrackRecord.stageMilestone = this.defaultTracks.FreeDefaultStageMilestones;
            break;
          case RewardsTrackType.SEASON_PASS_PAID:
            this.rewardsTrackRecord.paid = this.defaultTracks.PaidDefaultTrack;
            this.rewardsTrackRecord.stageMilestone = this.defaultTracks.PaidDefaultStageMilestones;
            break;
        }
        // Always enforce isTemplate for reward track templates
        this.rewardsTrackRecord.isTemplate = true;
      }
    }
    
    this.onRewardsTrackRecordChange.emit(this.rewardsTrackRecord);
  }
}
